import { createServerFn } from "@tanstack/react-start";
import { FALLBACK_CHECKOUT, FALLBACK_LOAD, serverError } from "@/lib/user-error";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { getStripe,
  getTierPriceId,
  getBuyARunPriceId,
  getTopUpPriceId,
  getOnePlanPriceId,
  getTopUp100PriceId,
  isAdminEmail, isAdminIdentity,
  TIERS,
  BUY_A_RUN,
  TOPUP,
  ONE_PLAN,
  TOPUP_100,
  type CreditTier, isAdminClaims } from "@/lib/stripe.server";
import type Stripe from "stripe";
import { requireEmailConfirmed } from "@/integrations/supabase/require-email-confirmed";

const tierEnum = z.enum(["starter", "growth", "scale"]);

type ReferralLookup =
  | { valid: true; id: string; code: string; bonusPct: number }
  | { valid: false; reason: "not_found" | "expired" | "already_redeemed" };

async function lookupReferralCode(
  rawCode: string,
  userId: string,
): Promise<ReferralLookup> {
  const normalized = rawCode.trim().toUpperCase();
  if (!normalized) return { valid: false, reason: "not_found" };

  const { data: row, error } = await supabaseAdmin
    .from("referral_codes")
    .select("id, code, bonus_pct, active, expires_at")
    .eq("code", normalized)
    .maybeSingle();
  if (error) throw serverError("billing:referral", error, FALLBACK_CHECKOUT);
  if (!row || !row.active) return { valid: false, reason: "not_found" };
  if (row.expires_at && new Date(row.expires_at as string) <= new Date()) {
    return { valid: false, reason: "expired" };
  }

  const { data: existing, error: rErr } = await supabaseAdmin
    .from("referral_redemptions")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (rErr) throw serverError("billing:referral-redemption", rErr, FALLBACK_CHECKOUT);
  if (existing) return { valid: false, reason: "already_redeemed" };

  return {
    valid: true,
    id: row.id as string,
    code: row.code as string,
    bonusPct: Number(row.bonus_pct) || 0,
  };
}

export const validateReferralCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ code: z.string().trim().min(1).max(64) }).parse(input),
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<
      | { valid: true; code: string; bonusPct: number }
      | { valid: false; reason: "not_found" | "expired" | "already_redeemed" }
    > => {
      const result = await lookupReferralCode(data.code, context.userId);
      if (!result.valid) return { valid: false, reason: result.reason };
      return { valid: true, code: result.code, bonusPct: result.bonusPct };
    },
  );

export type TierInfo = {
  tier: CreditTier;
  credits: number;
  runs: number;
  priceUsd: number;
  label: string;
  blurb: string;
};

export const getCreditTiers = createServerFn({ method: "GET" }).handler(
  async (): Promise<TierInfo[]> => {
    return (Object.values(TIERS) as TierInfo[]).map((t) => ({
      tier: t.tier,
      credits: t.credits,
      runs: t.runs,
      priceUsd: t.priceUsd,
      label: t.label,
      blurb: t.blurb,
    }));
  },
);

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireEmailConfirmed])
  .inputValidator((input) =>
    z
      .object({
        tier: tierEnum,
        referralCode: z.string().trim().min(1).max(64).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<
    | { url: string }
    | { referralError: "not_found" | "expired" | "already_redeemed" }
  > => {
    const { userId, claims } = context;
    const { assertEmailVerified } = await import(
      "@/lib/verification.functions"
    );
    await assertEmailVerified(userId);
    // Legacy tier values from stale clients are accepted and ignored: every
    // plan purchase now resolves to the single One Plan (500 credits / $500).
    const spec = ONE_PLAN;
    const priceId = getOnePlanPriceId();
    const req = getRequest();
    const origin = new URL(req.url).origin;
    const email =
      typeof claims?.email === "string" ? (claims.email as string) : undefined;

    const { data: teamId } = await supabaseAdmin.rpc("current_team_id", {
      _user: userId,
    });

    let bonusCredits = 0;
    let referralCodeNormalized: string | null = null;
    if (data.referralCode) {
      const referral = await lookupReferralCode(data.referralCode, userId);
      if (!referral.valid) {
        return { referralError: referral.reason };
      }
      bonusCredits = Math.floor((spec.credits * referral.bonusPct) / 100);
      referralCodeNormalized = referral.code;
    }

    const meta: Record<string, string> = {
      user_id: userId,
      tier: "one_plan",
      product_kind: "tier",
      credits_amount: String(spec.credits + bonusCredits),
      runs_amount: "0",
      ...(teamId ? { team_id: teamId as string } : {}),
      ...(referralCodeNormalized
        ? {
            referral_code: referralCodeNormalized,
            bonus_credits: String(bonusCredits),
          }
        : {}),
    };

    const stripe = getStripe();
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      line_items: [{ price: priceId, quantity: spec.credits }],
      success_url: `${origin}/buy-credits?status=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/buy-credits?status=cancelled`,
      customer_email: email,
      metadata: meta,
      payment_intent_data: { metadata: meta, statement_descriptor_suffix: "CAPITALDART" },
      branding_settings: { display_name: "CapitalDart" },
    });
    if (!session.url)
      throw serverError("billing:checkout-tier", "no session url", FALLBACK_CHECKOUT);
    return { url: session.url };
  });

export const createBuyARunCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireEmailConfirmed])
  .handler(async ({ context }): Promise<{ url: string }> => {
    const { userId, claims } = context;
    const { assertEmailVerified } = await import(
      "@/lib/verification.functions"
    );
    await assertEmailVerified(userId);
    const priceId = getBuyARunPriceId();
    const req = getRequest();
    const origin = new URL(req.url).origin;
    const email =
      typeof claims?.email === "string" ? (claims.email as string) : undefined;

    const stripe = getStripe();
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      line_items: [{ price: priceId, quantity: 1 }],
      success_url: `${origin}/buy-credits?status=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/buy-credits?status=cancelled`,
      customer_email: email,
      metadata: {
        user_id: userId,
        product_kind: "buy_a_run",
        credits_amount: "0",
        runs_amount: String(BUY_A_RUN.runs),
      },
      payment_intent_data: {
        metadata: {
          user_id: userId,
          product_kind: "buy_a_run",
          credits_amount: "0",
          runs_amount: String(BUY_A_RUN.runs),
        },
        statement_descriptor_suffix: "CAPITALDART",
      },
    });
    if (!session.url)
      throw serverError("billing:checkout", "no session url", FALLBACK_CHECKOUT);
    return { url: session.url };
  });

async function hasTierPurchase(userId: string): Promise<boolean> {
  // Team-broadened: any member of the caller's team having previously
  // purchased a tier package unlocks top-ups for the whole team.
  const { data: teamId, error: tErr } = await supabaseAdmin.rpc(
    "current_team_id",
    { _user: userId },
  );
  if (tErr) throw serverError("billing:team", tErr, FALLBACK_LOAD);
  let memberIds: string[] = [userId];
  if (teamId) {
    const { data: members, error: mErr } = await supabaseAdmin
      .from("team_members")
      .select("user_id")
      .eq("team_id", teamId as string);
    if (mErr) throw serverError("billing:team-members", mErr, FALLBACK_LOAD);
    if (members && members.length > 0) {
      memberIds = members.map((m) => m.user_id as string);
    }
  }
  const { data, error } = await supabaseAdmin
    .from("processed_stripe_events")
    .select("event_id")
    .in("user_id", memberIds)
    .eq("event_type", "checkout.session.completed")
    .contains("payload_summary", { product_kind: "tier" })
    .limit(1)
    .maybeSingle();
  if (error) throw serverError("billing:tier-purchase", error, FALLBACK_LOAD);
  return !!data;
}

export const getCanBuyTopUp = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ canBuyTopUp: boolean }> => {
    return { canBuyTopUp: await hasTierPurchase(context.userId) };
  });

export const createTopUpCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireEmailConfirmed])
  .handler(async ({ context }): Promise<{ url: string }> => {
    const { userId, claims } = context;
    const { assertEmailVerified } = await import(
      "@/lib/verification.functions"
    );
    await assertEmailVerified(userId);
    if (!(await hasTierPurchase(userId))) {
      throw new Error(
        "Top-ups are available after your first credit package purchase.",
      );
    }
    const priceId = getTopUp100PriceId();
    const req = getRequest();
    const origin = new URL(req.url).origin;
    const email =
      typeof claims?.email === "string" ? (claims.email as string) : undefined;

    const { data: teamId } = await supabaseAdmin.rpc("current_team_id", {
      _user: userId,
    });
    const meta = {
      user_id: userId,
      product_kind: "topup",
      credits_amount: String(TOPUP_100.credits),
      runs_amount: "0",
      ...(teamId ? { team_id: teamId as string } : {}),
    };

    const stripe = getStripe();
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      line_items: [{ price: priceId, quantity: TOPUP_100.credits }],
      success_url: `${origin}/buy-credits?status=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/buy-credits?status=cancelled`,
      customer_email: email,
      metadata: meta,
      payment_intent_data: { metadata: meta, statement_descriptor_suffix: "CAPITALDART" },
      branding_settings: { display_name: "CapitalDart" },
    });
    if (!session.url)
      throw serverError("billing:checkout", "no session url", FALLBACK_CHECKOUT);
    return { url: session.url };
  });

export const createOnePlanCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireEmailConfirmed])
  .inputValidator((input) =>
    z
      .object({
        referralCode: z.string().trim().min(1).max(64).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<
    | { url: string }
    | { referralError: "not_found" | "expired" | "already_redeemed" }
  > => {
    const { userId, claims } = context;
    const { assertEmailVerified } = await import(
      "@/lib/verification.functions"
    );
    await assertEmailVerified(userId);
    const spec = ONE_PLAN;
    const priceId = getOnePlanPriceId();
    const req = getRequest();
    const origin = new URL(req.url).origin;
    const email =
      typeof claims?.email === "string" ? (claims.email as string) : undefined;

    const { data: teamId } = await supabaseAdmin.rpc("current_team_id", {
      _user: userId,
    });

    let bonusCredits = 0;
    let referralCodeNormalized: string | null = null;
    if (data.referralCode) {
      const referral = await lookupReferralCode(data.referralCode, userId);
      if (!referral.valid) {
        return { referralError: referral.reason };
      }
      bonusCredits = Math.floor((spec.credits * referral.bonusPct) / 100);
      referralCodeNormalized = referral.code;
    }

    const meta: Record<string, string> = {
      user_id: userId,
      product_kind: "one_plan",
      plan_kind: "one_plan",
      credits_amount: String(spec.credits + bonusCredits),
      runs_amount: String(spec.runs),
      ...(teamId ? { team_id: teamId as string } : {}),
      ...(referralCodeNormalized
        ? {
            referral_code: referralCodeNormalized,
            bonus_credits: String(bonusCredits),
          }
        : {}),
    };

    const stripe = getStripe();
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      line_items: [{ price: priceId, quantity: spec.credits }],
      success_url: `${origin}/buy-credits?status=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/buy-credits?status=cancelled`,
      customer_email: email,
      metadata: meta,
      payment_intent_data: { metadata: meta, statement_descriptor_suffix: "CAPITALDART" },
      branding_settings: { display_name: "CapitalDart" },
    });
    if (!session.url)
      throw serverError("billing:checkout-one-plan", "no session url", FALLBACK_CHECKOUT);
    return { url: session.url };
  });

export const createTopUp100CheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireEmailConfirmed])
  .handler(async ({ context }): Promise<{ url: string }> => {
    const { userId, claims } = context;
    const { assertEmailVerified } = await import(
      "@/lib/verification.functions"
    );
    await assertEmailVerified(userId);
    if (!(await hasTierPurchase(userId))) {
      throw new Error(
        "Top-ups are available after your first credit package purchase.",
      );
    }
    const priceId = getTopUp100PriceId();
    const req = getRequest();
    const origin = new URL(req.url).origin;
    const email =
      typeof claims?.email === "string" ? (claims.email as string) : undefined;

    const { data: teamId } = await supabaseAdmin.rpc("current_team_id", {
      _user: userId,
    });
    const meta = {
      user_id: userId,
      product_kind: "topup",
      credits_amount: String(TOPUP_100.credits),
      runs_amount: String(TOPUP_100.runs),
      ...(teamId ? { team_id: teamId as string } : {}),
    };

    const stripe = getStripe();
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      line_items: [{ price: priceId, quantity: TOPUP_100.credits }],
      success_url: `${origin}/buy-credits?status=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/buy-credits?status=cancelled`,
      customer_email: email,
      metadata: meta,
      payment_intent_data: { metadata: meta, statement_descriptor_suffix: "CAPITALDART" },
      branding_settings: { display_name: "CapitalDart" },
    });
    if (!session.url)
      throw serverError("billing:checkout", "no session url", FALLBACK_CHECKOUT);
    return { url: session.url };
  });

export const getMyAdminStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ isAdmin: boolean; email: string | null }> => {
    const email =
      typeof context.claims?.email === "string"
        ? (context.claims.email as string)
        : null;
    return { isAdmin: isAdminClaims(context.claims), email };
  });

async function requireAdmin(claims: Record<string, unknown> | null | undefined): Promise<string> {
  const email =
    typeof claims?.email === "string" ? (claims.email as string) : null;
  if (!isAdminClaims(claims)) throw new Error("Forbidden: admin only");
  return email!;
}

export const grantCreditsAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        target_email: z.string().trim().email().max(320),
        amount: z.number().int().min(1).max(10000),
        reason: z.string().trim().max(500).optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<{ ok: true; target_user_id: string }> => {
    const adminEmail = await requireAdmin(context.claims as Record<string, unknown>);
    const targetEmail = data.target_email.toLowerCase();

    // Resolve email -> user_id via SECURITY DEFINER RPC (service_role only).
    const { data: rpcUserId, error: rpcErr } = await supabaseAdmin.rpc(
      "get_user_id_by_email",
      { p_email: targetEmail },
    );
    if (rpcErr) throw new Error(`Lookup failed: ${rpcErr.message}`);
    const targetUserId = (rpcUserId as string | null) ?? null;
    if (!targetUserId) {
      throw new Error(`No user found for email: ${data.target_email}`);
    }

    // Insert the admin grant record first so the ledger row can point at
    // its id. If the ledger call then fails, the grant row is deleted so
    // history stays consistent.
    const { data: grantRow, error: logErr } = await supabaseAdmin
      .from("admin_credit_grants")
      .insert({
        admin_email: adminEmail,
        target_user_id: targetUserId,
        amount: data.amount,
        reason: data.reason ?? null,
      })
      .select("id")
      .single();
    if (logErr || !grantRow) {
      throw new Error(`Grant log failed: ${logErr?.message ?? "no row"}`);
    }

    const { error: upErr } = await supabaseAdmin.rpc("record_credit_delta_user", {
      p_user_id: targetUserId,
      p_delta: data.amount,
      p_source: "admin_grant",
      p_reference: String(grantRow.id),
    });
    if (upErr) {
      await supabaseAdmin
        .from("admin_credit_grants")
        .delete()
        .eq("id", grantRow.id);
      throw new Error(`Credit update failed: ${upErr.message}`);
    }

    return { ok: true, target_user_id: targetUserId };
  });

export type AdminGrant = {
  id: number;
  admin_email: string;
  target_user_id: string;
  target_email: string | null;
  amount: number;
  reason: string | null;
  created_at: string;
};

// Batch-resolve auth emails for a list of user ids using the admin API.
// One getUserById call per unique id (mirrors admin-ledger's pattern).
async function resolveEmailsForUserIds(
  userIds: string[],
): Promise<Map<string, string | null>> {
  const unique = Array.from(new Set(userIds));
  const out = new Map<string, string | null>();
  await Promise.all(
    unique.map(async (uid) => {
      try {
        const { data } = await supabaseAdmin.auth.admin.getUserById(uid);
        out.set(uid, data?.user?.email ?? null);
      } catch {
        out.set(uid, null);
      }
    }),
  );
  return out;
}

export const getAdminGrants = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminGrant[]> => {
    await requireAdmin(context.claims as Record<string, unknown>);
    const { data, error } = await supabaseAdmin
      .from("admin_credit_grants")
      .select("id, admin_email, target_user_id, amount, reason, created_at")
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Omit<AdminGrant, "target_email">[];
    const emails = await resolveEmailsForUserIds(rows.map((r) => r.target_user_id));
    return rows.map((r) => ({ ...r, target_email: emails.get(r.target_user_id) ?? null }));
  });

export const grantRunsAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        target_email: z.string().trim().email().max(320),
        amount: z.number().int().min(1).max(1000),
        reason: z.string().trim().max(500).optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<{ ok: true; target_user_id: string }> => {
    const adminEmail = await requireAdmin(context.claims as Record<string, unknown>);
    const targetEmail = data.target_email.toLowerCase();

    const { data: rpcUserId, error: rpcErr } = await supabaseAdmin.rpc(
      "get_user_id_by_email",
      { p_email: targetEmail },
    );
    if (rpcErr) throw new Error(`Lookup failed: ${rpcErr.message}`);
    const targetUserId = (rpcUserId as string | null) ?? null;
    if (!targetUserId) {
      throw new Error(`No user found for email: ${data.target_email}`);
    }

    const { error: upErr } = await supabaseAdmin.rpc("increment_user_runs", {
      p_user_id: targetUserId,
      p_delta: data.amount,
    });
    if (upErr) throw new Error(`Run update failed: ${upErr.message}`);

    const { error: logErr } = await supabaseAdmin
      .from("admin_run_grants")
      .insert({
        admin_email: adminEmail,
        target_user_id: targetUserId,
        amount: data.amount,
        reason: data.reason ?? null,
      });
    if (logErr) throw new Error(`Grant log failed: ${logErr.message}`);

    return { ok: true, target_user_id: targetUserId };
  });

export const getAdminRunGrants = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminGrant[]> => {
    await requireAdmin(context.claims as Record<string, unknown>);
    const { data, error } = await supabaseAdmin
      .from("admin_run_grants")
      .select("id, admin_email, target_user_id, amount, reason, created_at")
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Omit<AdminGrant, "target_email">[];
    const emails = await resolveEmailsForUserIds(rows.map((r) => r.target_user_id));
    return rows.map((r) => ({ ...r, target_email: emails.get(r.target_user_id) ?? null }));
  });

// ─────────────────────────────────────────────────────────────────────────────
// Harness credit grants (admin-only, team-scoped).
//
// Adds a signed grant to team_credits.credits_remaining for a specified team
// and logs it to admin_credit_grants with team_id set. Used exclusively by
// the pipeline regression harness (/admin/harness) to pre-fund harness runs
// so the normal chargeRun path can bill them without a change to chargeRun.
//
// Guardrails:
//   - admin-only (isAdminEmail on claims.email).
//   - reason must match /^harness_(seed|crash_resume):<uuid>$/ for audit.
//   - amount capped to 10_000 credits per call.
// ─────────────────────────────────────────────────────────────────────────────

const HARNESS_REASON_RE =
  /^harness_(seed|crash_resume):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const adminGrantCreditsForHarness = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        teamId: z.string().uuid(),
        amount: z.number().int().min(1).max(10_000),
        reason: z.string().regex(HARNESS_REASON_RE, {
          message: "reason must be 'harness_seed:<uuid>' or 'harness_crash_resume:<uuid>'",
        }),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<{ ok: true; new_balance: number }> => {
    const adminEmail = await requireAdmin(context.claims as Record<string, unknown>);

    // Insert grant record first so the ledger reference is the grant id.
    const { data: grantRow, error: logErr } = await supabaseAdmin
      .from("admin_credit_grants")
      .insert({
        admin_email: adminEmail,
        target_user_id: context.userId,
        team_id: data.teamId,
        amount: data.amount,
        reason: data.reason,
      })
      .select("id")
      .single();
    if (logErr || !grantRow) {
      throw new Error(`Harness grant log failed: ${logErr?.message ?? "no row"}`);
    }

    const { data: newBal, error: upErr } = await supabaseAdmin.rpc(
      "record_credit_delta_team",
      {
        p_team_id: data.teamId,
        p_delta: data.amount,
        p_source: "admin_grant",
        p_reference: String(grantRow.id),
        p_user_id: context.userId,
      },
    );
    if (upErr) {
      await supabaseAdmin
        .from("admin_credit_grants")
        .delete()
        .eq("id", grantRow.id);
      throw new Error(`team_credits update failed: ${upErr.message}`);
    }

    return { ok: true as const, new_balance: Number(newBal) || 0 };
  });

// ─────────────────────────────────────────────────────────────────────────────
// User-facing purchase history (Billing tab).
// ─────────────────────────────────────────────────────────────────────────────

export type PurchaseHistoryRow = {
  id: string;
  date: string;
  productLabel: string;
  credits: number;
  runs: number;
  amountCents: number | null;
  currency: string | null;
  status: "succeeded";
  sessionId: string | null;
};

function productLabel(summary: Record<string, unknown>): string {
  const kind = (summary.product_kind as string | undefined) ?? "tier";
  if (kind === "buy_a_run") return "Buy-a-Run";
  const tier = (summary.tier as string | undefined) ?? "";
  if (tier === "starter") return "Starter tier";
  if (tier === "growth") return "Growth tier";
  if (tier === "scale") return "Scale tier";
  return "Purchase";
}

export const getMyPurchaseHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PurchaseHistoryRow[]> => {
    const { userId } = context;
    const { data, error } = await supabaseAdmin
      .from("processed_stripe_events")
      .select("event_id, event_type, processed_at, payload_summary")
      .eq("user_id", userId)
      .eq("event_type", "checkout.session.completed")
      .order("processed_at", { ascending: false })
      .limit(50);
    if (error) throw serverError("billing:purchase-history", error, FALLBACK_LOAD);
    return (data ?? []).map((row) => {
      const s = (row.payload_summary ?? {}) as Record<string, unknown>;
      return {
        id: row.event_id as string,
        date: row.processed_at as string,
        productLabel: productLabel(s),
        credits: Number(s.credits ?? 0) || 0,
        runs: Number(s.runs ?? 0) || 0,
        amountCents:
          typeof s.amount_total === "number" ? (s.amount_total as number) : null,
        currency: typeof s.currency === "string" ? (s.currency as string) : null,
        status: "succeeded",
        sessionId: typeof s.session_id === "string" ? (s.session_id as string) : null,
      } satisfies PurchaseHistoryRow;
    });
  });

export const getStripeReceiptUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ sessionId: z.string().trim().min(1).max(200) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<{ url: string | null }> => {
    const { userId } = context;
    // Ownership: confirm this session belongs to the calling user before
    // hitting Stripe (don't let signed-in users probe arbitrary session ids).
    const { data: own, error: ownErr } = await supabaseAdmin
      .from("processed_stripe_events")
      .select("event_id")
      .eq("user_id", userId)
      .contains("payload_summary", { session_id: data.sessionId })
      .limit(1)
      .maybeSingle();
    if (ownErr) throw serverError("billing:receipt", ownErr, FALLBACK_LOAD);
    if (!own) throw new Error("Receipt not found for this account.");

    const stripe = getStripe();
    const session = await stripe.checkout.sessions.retrieve(data.sessionId, {
      expand: ["payment_intent.latest_charge"],
    });
    const pi = session.payment_intent as Stripe.PaymentIntent | string | null;
    let receiptUrl: string | null = null;
    if (pi && typeof pi === "object") {
      const charge = (pi as Stripe.PaymentIntent).latest_charge as
        | Stripe.Charge
        | string
        | null;
      if (charge && typeof charge === "object") {
        receiptUrl = (charge as Stripe.Charge).receipt_url ?? null;
      }
    }
    return { url: receiptUrl };
  });